"use client";

import { InfoCircle } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import type * as React from "react";
import { cn } from "@/lib/utils";
import {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipProvider,
  HybridTooltipTrigger,
} from "./hybrid-tooltip";

export interface InfoTooltipProps {
  /** Tooltip body — usually Persian explanatory copy. */
  children: React.ReactNode;
  /** Icon pixel size. Default 16. */
  iconSize?: number;
  /** Tooltip side. Default "bottom". */
  side?: "top" | "bottom" | "left" | "right";
  /** Extra classes on the icon trigger. */
  className?: string;
  /** Extra classes on the tooltip content. */
  contentClassName?: string;
}

/**
 * Reusable info tooltip primitive.
 *
 * Wraps the existing {@link HybridTooltip} (click on touch devices, hover on desktop)
 * so a contextual explanation sits next to any label. Accessible: the icon is a real
 * <button> with `aria-label="راهنما"` and joins the tab order. Press Enter/Space to open
 * on desktop; tap anywhere to toggle on mobile.
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  children,
  iconSize = 16,
  side = "bottom",
  className,
  contentClassName,
}) => {
  const t = useTranslations("common.form");
  return (
    <HybridTooltipProvider>
      <HybridTooltip>
        <HybridTooltipTrigger asChild>
          <button
            type="button"
            aria-label={t("helpAria")}
            className={cn(
              "inline-flex items-center justify-center text-text-secondary",
              "rounded-full focus-visible:outline-none focus-visible:ring-1",
              "focus-visible:ring-ring transition-colors hover:text-text-primary",
              "cursor-pointer",
              className,
            )}
          >
            <InfoCircle
              size={iconSize}
              className="shrink-0"
              aria-hidden="true"
            />
          </button>
        </HybridTooltipTrigger>
        <HybridTooltipContent
          side={side}
          className={cn(
            "max-w-72 text-xs leading-relaxed text-center",
            contentClassName,
          )}
        >
          {children}
        </HybridTooltipContent>
      </HybridTooltip>
    </HybridTooltipProvider>
  );
};
